/*
 * File: /src/util/assetUtils.js
 * Created Date: Tuesday December 17th 2024
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Tuesday, 17th December 2024 9:09:19 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2024 AdBase, Inc.
 */

import { authedAxios } from 'src/shared/axiosWrapper.js';
import { AD_BASE_ASSETS_PATH } from 'src/constants.js';

// Mapped from the defined types on the BE
// Eventually pull this from the API
export const ASSET_CONFIG = {
  audio: {
    extensions: {
      mp3: 'audio/mpeg',
      ogg: 'audio/ogg',
      oga: 'audio/ogg',
      wav: 'audio/wav',
    }
  },
  image: {
    extensions: {
      gif: 'image/gif',
      ico: 'image/vnd.microsoft.icon',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      svg: 'image/svg+xml',
      webp: 'image/webp',
    }
  },
  video: {
    extensions: {
      mp4: 'video/mp4',
      ogv: 'video/ogg',
      webm: 'video/webm',
    }
  },
  pdf: {
    extensions: {
     pdf: 'application/pdf',
    }
  },
  text: {
    extensions: {
      md: 'text/markdown',
      txt: 'text/plain',
    }
  },
  doc: {
    extensions: {
     doc: 'application/msword',
     docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
     rtf: 'application/rtf',
     odt: 'application/vnd.oasis.opendocument.text',
    }
  },
  spreadsheet: {
    extensions: {
      csv: 'text/csv',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  },
  html: {
    extensions: {
      htm: 'text/html',
      html: 'text/html',
    }
  },
  zip: {
    extensions: {
      zip: 'application/zip', // windows uses application/x-zip-compressed
      '7z': 'application/x-7z-compressed',
    }
  },
  binary: {
    extensions: {
      bin: 'application/octet-stream',
    }
  },
};

const _mimeToAssetType = {};
for (const [assetType, config] of Object.entries(ASSET_CONFIG)) {
  for (const mimeType of Object.values(config.extensions)) {
    _mimeToAssetType[mimeType] = assetType;
  }
}

function _getS2AssetType(rawFile) {
  if (!rawFile.type) {
    console.error('Unexpected File format');
    return {
      success: false,
      error: 'Unexpected File Format, can not process file',
    };
  }
  return _mimeToAssetType[rawFile.type] || 'binary';
}

function _getUploadEndpoint(file) {
  if (file.isVerification) {
    return 'upload-verification-url';
  }
  if (file.isUserPhoto) {
    return 'upload-user-photo-url';
  }
  return 'upload-url';
}

// Takes a raw file object not a S2 file object
// But it does require some specific S2 only attributes to be set on the rawFile
// that are not part of the rawFile definition: isPublic, isVerification, etc
export async function uploadAsset(file) {
  // If we dont provide isPublic as false is defaults to public.
  // Seems dangerous to assume public if a dev forgets to add the attribute.
  // An error seems more appropriate
  if (file.isPublic == undefined) {
    console.error('File does not have isPublic attribute set');
    return {
      success: false,
      error: 'Unable to process file; missing required attrubte',
    };
  }
  let result;
  try {
    // Get signed URL for upload
    const endpoint = _getUploadEndpoint(file);
    result = await authedAxios.post(`${AD_BASE_ASSETS_PATH}${endpoint}`, {
      name: file.name,
      is_public: file.isPublic,
      mime_type: file.type,
      size: file.size,
      asset_type: _getS2AssetType(file),
    });
  } catch(e) {
    console.error('Error Getting signed url:', e);
    return {
      success: false,
      error: e,
    };
  }

  try {
    // Upload to S3
    await fetch(result.data.url, {
      method: 'PUT',
      cache: 'no-cache',
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: file,
    });
  } catch (error) {
    console.error('Error Saving asset to server:', error);
    return {
      success: false,
      error,
    };
  }

  try {
    // Confirm upload
    const confirmResponse = await authedAxios.put(
      `${AD_BASE_ASSETS_PATH}${result.data.id}/upload-complete`
    );
    return {
      success: true,
      asset: confirmResponse.data,
    };
  } catch (error) {
    console.error('Error Confimring asset saved:', error);
    return {
      success: false,
      error,
    };
  }
}

export function getAcceptedFileTypes(assetTypes, allowedExtensions) {
  let allowedTypes = new Set();
  assetTypes.forEach((a) => Object.values(ASSET_CONFIG[a].extensions).forEach((e) =>
    !allowedExtensions ? allowedTypes.add(e) : !allowedExtensions.includes(e) ? allowedTypes.add(e) : undefined
  ));
  return [...allowedTypes].join(',');
}
